import { useEffect, useRef, useState, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { authArrival, type AuthArrival } from './authArrival';
import { useSession } from './SessionProvider';

export interface ArrivalRouterProps {
  arrival: AuthArrival;
  session: boolean;
  loading: boolean;
  /** Shortened by the tests, which have no SDK to wait for. */
  graceMs?: number;
}

/**
 * Puts an arrival on the screen it belongs on, whatever route it landed on.
 *
 * GoTrue picks the landing address from the project's allow list and falls back
 * to the Site URL when the address we asked for is not on it. That put a
 * signed-in buyer on the sale page, which looks exactly like the page a
 * stranger sees, so it read as "the link did not work".
 *
 * It waits for the session to resolve before moving. Navigating first would
 * rewrite the URL and take the fragment away from the SDK before it had read
 * the token out of it, turning a working link into a broken one.
 *
 * Split from the component below so the decision can be tested without a
 * provider, a network and a real fragment.
 */
/**
 * How long a session arrival may take to become a session before it is treated
 * as a dead link.
 *
 * `loading` turning false is not the same as "the fragment has been read":
 * `getSession()` can answer null from storage and clear the flag while the SDK
 * is still exchanging the token in the URL. On a desktop those land in the same
 * frame; on a phone on mobile data they do not, and calling it dead early sends
 * a buyer holding a perfectly good link to the resend form.
 */
const SESSION_GRACE_MS = 4000;

export function ArrivalRouter({
  arrival,
  session,
  loading,
  graceMs = SESSION_GRACE_MS
}: ArrivalRouterProps): JSX.Element | null {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [graceOver, setGraceOver] = useState(false);
  /**
   * An arrival is a one-time event, but it is read from the fragment at boot,
   * so the value stays true for as long as the page lives. Without this the
   * effect re-fired on every navigation and dragged her back to the arrival
   * screen, which forwarded her on, which re-fired it.
   */
  const spent = useRef(false);

  useEffect(() => {
    if (arrival?.kind !== 'session') {
      return;
    }
    const timer = setTimeout(() => setGraceOver(true), graceMs);
    return () => clearTimeout(timer);
  }, [arrival, graceMs]);

  useEffect(() => {
    if (!arrival || loading || spent.current) {
      return;
    }

    if (arrival.kind === 'session') {
      if (session) {
        spent.current = true;
        const destination = arrival.purpose === 'recovery' ? '/senha/definir' : '/acesso';
        if (pathname !== destination) {
          navigate(destination, { replace: true });
        }
        return;
      }
      // Still waiting. A token that never resolves is handled below, once the
      // grace has run out.
      if (!graceOver) {
        return;
      }
    }

    // Either the link carried an error, or it carried a token that no longer
    // buys a session: a single-use link opened once already, which a scanner in
    // the mail path does routinely. Both are the same thing to her, and the
    // answer to both is a new link.
    spent.current = true;
    if (pathname !== '/reenviar') {
      navigate('/reenviar', { replace: true, state: { linkFailed: true } });
    }
  }, [arrival, loading, session, graceOver, pathname, navigate]);

  return null;
}

export function AuthArrivalRedirect(): JSX.Element | null {
  const { session, loading } = useSession();
  return <ArrivalRouter arrival={authArrival()} session={session !== null} loading={loading} />;
}
